import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Copy, ChevronUp, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ProgramComponent, VideoSeriesComponent, RepeatingTaskComponent, StepsComponent, DynamicTaskComponent, VideoTask, DynamicTask } from '@/pages/add-program';
import InputTypeSelector from './input-type-selector';
import VideoContentSelector, { parseDuration } from './video-content-selector';
import { ImageUpload } from '@/components/image-upload';
import { getContentByIds } from '@/lib/firebase';

interface ComponentModalProps {
  component: ProgramComponent | null;
  onSave: (component: ProgramComponent) => void;
  onCancel: () => void;
  contentLibrary: string;
}

export default function ComponentModal({ component, onSave, onCancel, contentLibrary }: ComponentModalProps) {
  const [componentType, setComponentType] = useState<'videoSeries' | 'repeatingTask' | 'steps' | 'dynamicTask'>(
    component?.componentType || 'videoSeries'
  );
  const [startOnDay, setStartOnDay] = useState<number>(
    component?.startOnDay || 1
  );

  // VideoSeries state
  const [videoSeriesStep, setVideoSeriesStep] = useState(1); // Step 1: Select, Step 2: Reorder
  const [selectedVideos, setSelectedVideos] = useState<VideoTask[]>(
    component?.componentType === 'videoSeries' ? component.tasks : []
  );
  
  // Track raw content items for video selector
  const [selectedContentItems, setSelectedContentItems] = useState<any[]>([]);
  
  // Track ordered videos for step 2
  const [orderedVideos, setOrderedVideos] = useState<any[]>([]);
  
  // Load existing video data when editing a videoSeries component
  useEffect(() => {
    const loadExistingVideos = async () => {
      if (component?.componentType === 'videoSeries' && component.tasks.length > 0) {
        try {
          // Get unique content IDs
          const uniqueContentIds = Array.from(new Set(component.tasks.map(task => task.contentId)));
          const contentItems = await getContentByIds(uniqueContentIds) as any[];
          const contentMap = new Map(
            contentItems.map(data => {
              return [data.id, { 
                ...data,
                duration: typeof data.duration === 'string' ? parseDuration(data.duration) : (data.duration || 0)
              }];
            })
          );
          
          // Reconstruct ordered videos preserving duplicates and order
          const orderedContent = component.tasks.map(task => {
            const baseContent = contentMap.get(task.contentId) as any;
            if (baseContent) {
              // Preserve the task's unique ID (which may be a duplicate ID)
              return {
                ...baseContent,
                id: task.id, // This preserves duplicate IDs like "xxx_duplicate_123"
                contentId: task.contentId // Original content ID
              };
            }
            // Fallback if content not found
            return {
              id: task.id,
              contentId: task.contentId,
              title: task.title,
              externalId: task.externalId,
              duration: task.durationMinutes
            };
          });
          
          setOrderedVideos(orderedContent);
          // For selection, only include unique videos
          const uniqueContent = Array.from(contentMap.values());
          setSelectedContentItems(uniqueContent);
          setVideoSeriesStep(2);
        } catch (error) {
          captureHandledException(error, "program-component.load-content");
          // If loading fails, fall back to constructing from tasks
          const fallbackContent = component.tasks.map(task => ({
            id: task.id,
            contentId: task.contentId || task.id,
            title: task.title,
            externalId: task.externalId,
            duration: task.durationMinutes
          }));
          setOrderedVideos(fallbackContent);
          setSelectedContentItems(fallbackContent);
          setVideoSeriesStep(2);
        }
      }
    };
    
    loadExistingVideos();
  }, [component]);

  // RepeatingTask state - now uses tasks array like dynamicTask
  const [repeatingTasks, setRepeatingTasks] = useState<DynamicTask[]>(
    component?.componentType === 'repeatingTask' && Array.isArray(component.tasks)
      ? component.tasks
      : component?.componentType === 'repeatingTask'
        ? [{ id: crypto.randomUUID(), taskNumber: 1, taskMessage: (component as any).taskMessage || '', inputType: (component as any).inputType || 'checkBox' }]
        : [{ id: crypto.randomUUID(), taskNumber: 1, taskMessage: '', inputType: 'checkBox' }]
  );

  // Steps state
  const [target, setTarget] = useState(
    component?.componentType === 'steps' ? component.target : 10000
  );

  // DynamicTask state - IDs should already be normalized by parent
  const [dynamicTasks, setDynamicTasks] = useState<DynamicTask[]>(
    component?.componentType === 'dynamicTask' ? component.tasks : []
  );

  const handleSave = () => {
    let newComponent: ProgramComponent;

    switch (componentType) {
      case 'videoSeries':
        // Convert ordered videos to VideoTask format
        const videoTasks: VideoTask[] = orderedVideos.map((item, index) => {
          // Parse duration - handle both string (HH:MM:SS) and number formats
          let durationMinutes = 0;
          if (typeof item.duration === 'string') {
            durationMinutes = parseDuration(item.duration); // Already rounds up
          } else if (typeof item.duration === 'number') {
            durationMinutes = Math.ceil(item.duration); // Round up if it's a decimal
          }
          
          return {
            contentId: item.contentId || item.id, // Use contentId for the actual content reference
            durationMinutes,
            externalId: item.externalId,
            id: item.id, // Use the unique ID (may include _duplicate suffix)
            taskNumber: index + 1,
            title: item.title,
            thumbnailUrl: item.thumbnailUrl || '' // Include thumbnail URL from content
          };
        });
        
        newComponent = {
          componentType: 'videoSeries',
          componentOrder: component?.componentOrder || 1,
          startOnDay,
          tasks: videoTasks
        } as VideoSeriesComponent;
        break;

      case 'repeatingTask':
        newComponent = {
          componentType: 'repeatingTask',
          componentOrder: component?.componentOrder || 1,
          startOnDay,
          tasks: repeatingTasks
        } as RepeatingTaskComponent;
        break;

      case 'steps':
        newComponent = {
          componentType: 'steps',
          componentOrder: component?.componentOrder || 1,
          startOnDay,
          target
        } as StepsComponent;
        break;

      case 'dynamicTask':
        // Include id field for stable keys across sessions
        newComponent = {
          componentType: 'dynamicTask',
          componentOrder: component?.componentOrder || 1,
          startOnDay,
          tasks: dynamicTasks
        } as DynamicTaskComponent;
        break;
    }

    onSave(newComponent);
  };

  const handleAddRepeatingTask = () => {
    const newTaskNumber = repeatingTasks.length + 1;
    setRepeatingTasks([...repeatingTasks, {
      id: crypto.randomUUID(),
      taskNumber: newTaskNumber,
      taskMessage: '',
      inputType: 'checkBox',
      thumbnailUrl: ''
    }]);
  };

  const handleUpdateRepeatingTask = (index: number, updates: Partial<DynamicTask>) => {
    const updated = repeatingTasks.map((task, i) =>
      i === index ? { ...task, ...updates } : task
    );
    setRepeatingTasks(updated);
  };

  const handleDeleteRepeatingTask = (index: number) => {
    const updated = repeatingTasks.filter((_, i) => i !== index);
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
    }));
    setRepeatingTasks(renumbered);
  };

  const handleMoveRepeatingTaskUp = (index: number) => {
    if (index === 0) return;
    const updated = [...repeatingTasks];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
    }));
    setRepeatingTasks(renumbered);
  };

  const handleMoveRepeatingTaskDown = (index: number) => {
    if (index === repeatingTasks.length - 1) return;
    const updated = [...repeatingTasks];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
    }));
    setRepeatingTasks(renumbered);
  };

  const handleAddDynamicTask = () => {
    const newTaskNumber = dynamicTasks.length + 1;
    setDynamicTasks([...dynamicTasks, {
      id: crypto.randomUUID(),
      taskNumber: newTaskNumber,
      taskMessage: '',
      inputType: 'checkBox',
      thumbnailUrl: ''
    }]);
  };

  const handleUpdateDynamicTask = (index: number, updates: Partial<DynamicTask>) => {
    const updated = dynamicTasks.map((task, i) =>
      i === index ? { ...task, ...updates } : task
    );
    setDynamicTasks(updated);
  };

  const handleDeleteDynamicTask = (index: number) => {
    const updated = dynamicTasks.filter((_, i) => i !== index);
    // Renumber tasks but PRESERVE existing _id values
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
      // Keep existing _id, don't regenerate
    }));
    setDynamicTasks(renumbered);
  };

  const handleMoveDynamicTaskUp = (index: number) => {
    if (index === 0) return;
    const updated = [...dynamicTasks];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    // Renumber tasks
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
    }));
    setDynamicTasks(renumbered);
  };

  const handleMoveDynamicTaskDown = (index: number) => {
    if (index === dynamicTasks.length - 1) return;
    const updated = [...dynamicTasks];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    // Renumber tasks
    const renumbered = updated.map((task, i) => ({
      ...task,
      taskNumber: i + 1
    }));
    setDynamicTasks(renumbered);
  };

  // VideoSeries Step 2 handlers
  const handleMoveVideoUp = (index: number) => {
    if (index === 0) return;
    const updated = [...orderedVideos];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setOrderedVideos(updated);
  };

  const handleMoveVideoDown = (index: number) => {
    if (index === orderedVideos.length - 1) return;
    const updated = [...orderedVideos];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setOrderedVideos(updated);
  };

  const handleDuplicateVideo = (index: number) => {
    const original = orderedVideos[index];
    // Generate unique ID for the duplicate
    const duplicateId = `${original.contentId || original.id}_duplicate_${Date.now()}`;
    const videoCopy = { 
      ...original,
      id: duplicateId,
      contentId: original.contentId || original.id // Preserve the original content ID
    };
    const updated = [
      ...orderedVideos.slice(0, index + 1),
      videoCopy,
      ...orderedVideos.slice(index + 1)
    ];
    setOrderedVideos(updated);
  };

  const handleDeleteVideo = (index: number) => {
    const updated = orderedVideos.filter((_, i) => i !== index);
    setOrderedVideos(updated);
  };

  const handleNextStep = () => {
    if (videoSeriesStep === 1) {
      // Moving from step 1 to step 2 - initialize ordered videos
      // Ensure each video has both id and contentId
      const initialOrdered = selectedContentItems.map(item => ({
        ...item,
        contentId: item.id // Set contentId to the original ID
      }));
      setOrderedVideos(initialOrdered);
      setVideoSeriesStep(2);
    }
  };

  const handleBackStep = () => {
    if (videoSeriesStep === 2) {
      setVideoSeriesStep(1);
    }
  };

  const isValid = () => {
    switch (componentType) {
      case 'videoSeries':
        if (videoSeriesStep === 1) {
          return selectedContentItems.length > 0;
        } else {
          return orderedVideos.length > 0;
        }
      case 'repeatingTask':
        return repeatingTasks.length > 0 && 
               repeatingTasks.every(t => t.taskMessage.trim() !== '' && t.inputType.trim() !== '');
      case 'steps':
        return target > 0;
      case 'dynamicTask':
        return dynamicTasks.length > 0 && 
               dynamicTasks.every(t => t.taskMessage.trim() !== '' && t.inputType.trim() !== '');
      default:
        return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Buttons */}
      <div className="flex justify-end gap-2 pb-4 border-b">
        {componentType === 'videoSeries' && videoSeriesStep === 2 ? (
          <>
            <Button variant="outline" onClick={handleBackStep} data-testid="button-back-step">
              Back
            </Button>
            <Button onClick={handleSave} disabled={!isValid()} data-testid="button-save-component">
              {component ? 'Update Component' : 'Add Component'}
            </Button>
          </>
        ) : componentType === 'videoSeries' && videoSeriesStep === 1 ? (
          <>
            <Button variant="outline" onClick={onCancel} data-testid="button-cancel-component">
              Cancel
            </Button>
            <Button onClick={handleNextStep} disabled={!isValid()} data-testid="button-next-step">
              Next
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onCancel} data-testid="button-cancel-component">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isValid()} data-testid="button-save-component">
              {component ? 'Update Component' : 'Add Component'}
            </Button>
          </>
        )}
      </div>

      <div>
        <Label>Component Type</Label>
        <Select
          value={componentType}
          onValueChange={(value: any) => setComponentType(value)}
          disabled={!!component} // Disable type change when editing
        >
          <SelectTrigger data-testid="select-component-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="videoSeries">Video Series</SelectItem>
            <SelectItem value="repeatingTask">Repeating Task</SelectItem>
            <SelectItem value="steps">Steps</SelectItem>
            <SelectItem value="dynamicTask">Dynamic Task</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Start on Day</Label>
        <Input
          type="number"
          min="1"
          value={startOnDay}
          onChange={(e) => setStartOnDay(parseInt(e.target.value) || 1)}
          placeholder="1"
          data-testid="input-start-on-day"
        />
      </div>

      {componentType === 'videoSeries' && (
        <div className="space-y-4">
          {videoSeriesStep === 1 ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-lg font-semibold">Step 1: Select Videos</Label>
              </div>
              <VideoContentSelector
                selectedContent={selectedContentItems}
                onSelectionChange={setSelectedContentItems}
                contentLibrary={contentLibrary}
              />
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-lg font-semibold">Step 2: Reorder & Duplicate Videos</Label>
                <div className="text-sm text-muted-foreground">
                  {orderedVideos.length} video{orderedVideos.length !== 1 ? 's' : ''}
                </div>
              </div>
              
              {orderedVideos.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No videos selected
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {orderedVideos.map((video, index) => (
                    <Card key={`${video.id}-${index}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveVideoUp(index)}
                              disabled={index === 0}
                              className="h-6 w-6 p-0"
                              data-testid={`button-move-up-${index}`}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveVideoDown(index)}
                              disabled={index === orderedVideos.length - 1}
                              className="h-6 w-6 p-0"
                              data-testid={`button-move-down-${index}`}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                              <h4 className="font-medium text-sm truncate">{video.title}</h4>
                            </div>
                            {video.author && (
                              <p className="text-xs text-muted-foreground mt-1">by {video.author}</p>
                            )}
                          </div>
                          
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDuplicateVideo(index)}
                              data-testid={`button-duplicate-${index}`}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteVideo(index)}
                              data-testid={`button-delete-video-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {componentType === 'repeatingTask' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Tasks</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddRepeatingTask}
              data-testid="button-add-repeating-task"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </div>

          {repeatingTasks.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tasks added yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {repeatingTasks.map((task, index) => (
                <Card key={task.id || index}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Task #{task.taskNumber}</Label>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveRepeatingTaskUp(index)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveRepeatingTaskDown(index)}
                            disabled={index === repeatingTasks.length - 1}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRepeatingTask(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label>Task Message</Label>
                        <Input
                          value={task.taskMessage}
                          onChange={(e) => handleUpdateRepeatingTask(index, { taskMessage: e.target.value })}
                          placeholder="e.g., Drink 2L water"
                        />
                      </div>

                      <div>
                        <InputTypeSelector
                          value={task.inputType}
                          onChange={(value) => handleUpdateRepeatingTask(index, { inputType: value })}
                          label="Input Type"
                        />
                      </div>

                      <div>
                        <ImageUpload
                          value={task.thumbnailUrl || ''}
                          onChange={(url) => handleUpdateRepeatingTask(index, { thumbnailUrl: url })}
                          label="Thumbnail Image (optional)"
                          required={false}
                          uploadType="task"
                          taskId={task.id}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {componentType === 'steps' && (
        <div>
          <Label>Target Steps</Label>
          <Input
            type="number"
            value={target}
            onChange={(e) => setTarget(parseInt(e.target.value) || 0)}
            placeholder="10000"
            data-testid="input-target-steps"
          />
        </div>
      )}

      {componentType === 'dynamicTask' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Tasks</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddDynamicTask}
              data-testid="button-add-dynamic-task"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </div>

          {dynamicTasks.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tasks added yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {dynamicTasks.map((task, index) => (
                <Card key={task.id || index}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Task #{task.taskNumber}</Label>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDynamicTaskUp(index)}
                            disabled={index === 0}
                            data-testid={`button-move-dynamic-task-up-${index}`}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDynamicTaskDown(index)}
                            disabled={index === dynamicTasks.length - 1}
                            data-testid={`button-move-dynamic-task-down-${index}`}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDynamicTask(index)}
                            data-testid={`button-delete-dynamic-task-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label>Task Message</Label>
                        <Input
                          value={task.taskMessage}
                          onChange={(e) => handleUpdateDynamicTask(index, { taskMessage: e.target.value })}
                          placeholder="What do you want to ask?"
                          data-testid={`input-dynamic-task-message-${index}`}
                        />
                      </div>

                      <div>
                        <InputTypeSelector
                          value={task.inputType}
                          onChange={(value) => handleUpdateDynamicTask(index, { inputType: value })}
                          label="Input Type"
                        />
                      </div>

                      <div>
                        <ImageUpload
                          value={task.thumbnailUrl || ''}
                          onChange={(url) => handleUpdateDynamicTask(index, { thumbnailUrl: url })}
                          label="Thumbnail Image (optional)"
                          required={false}
                          uploadType="task"
                          taskId={task.id}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
